import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ToggleSwitch } from '@/shared/components/custom/toggle-switch/ToggleSwitch';
import { useUpdateLinkedCalendar } from '@/features/calendar/public';
import type { LinkedCalendar } from '@/features/calendar/public';
import { LinkedCalendarScopeDialog } from './LinkedCalendarScopeDialog';

interface LinkedCalendarRowProps {
  calendar: LinkedCalendar;
  connectionId: string;
}

export function LinkedCalendarRow({ calendar, connectionId }: LinkedCalendarRowProps) {
  const { t } = useTranslation('calendar');
  const updateCalendar = useUpdateLinkedCalendar();
  const [dialogOpen, setDialogOpen] = useState(false);

  function handleSyncToggle(syncEnabled: boolean) {
    updateCalendar.mutate({
      connectionId,
      calendarId: calendar.id,
      payload: {
        scope: calendar.scope,
        tenantId: calendar.tenantId,
        syncEnabled,
        colorHex: calendar.colorHex,
      },
    });
  }

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
      data-testid={`settings-integrations-calendar-row-${calendar.id}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          className="size-3 shrink-0 rounded-full"
          style={{ background: calendar.colorHex ?? 'var(--muted-foreground)' }}
          aria-hidden="true"
        />
        <span className="truncate text-sm text-foreground">{calendar.name}</span>
        {calendar.isPrimary && (
          <Badge variant="secondary" className="text-xs">
            {t('integrations.calendarRow.primary')}
          </Badge>
        )}
        <Badge variant="outline" className="text-xs">
          {calendar.scope === 'Global'
            ? t('integrations.calendarRow.scopeGlobal')
            : t('integrations.calendarRow.scopeTenant')}
        </Badge>
      </div>

      <div className="flex items-center gap-2">
        <ToggleSwitch
          id={`sync-${calendar.id}`}
          label={t('integrations.calendarRow.sync')}
          checked={calendar.syncEnabled}
          onCheckedChange={handleSyncToggle}
          disabled={updateCalendar.isPending}
          data-testid={`settings-integrations-sync-toggle-${calendar.id}`}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t('integrations.calendarRow.edit')}
              onClick={() => setDialogOpen(true)}
              data-testid={`settings-integrations-edit-calendar-${calendar.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('integrations.calendarRow.edit')}</TooltipContent>
        </Tooltip>
      </div>

      <LinkedCalendarScopeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        calendar={calendar}
        connectionId={connectionId}
      />
    </div>
  );
}
